import { useCallback } from 'react';

import type { SelectOption } from '@swf/components/Inputs/components/DropDown/DropDown';
import type { DropDownOptionValue } from '@swf/components/Inputs/components/DropDown/DropDownOption';

export interface FormData {
  [key: string | symbol]: DropDownOptionValue | boolean | undefined;
}

export interface Props<T extends FormData> {
  values: T;
  change: <F extends string>(name: F, value?: FormData[F]) => void;
}

export interface FieldProps<T extends FormData> {
  field: keyof T;
  options: SelectOption[];
}

const useForceUpdate = <T extends FormData>({ values, change }: Props<T>) => {
  const forceFieldUpdate = useCallback(
    ({ field, options }: FieldProps<T>) => {
      const hasNoInitialValue = typeof values[field] === 'undefined';
      const isMissingInOptions =
        typeof options.find(({ value }) => value === values[field]) === 'undefined';
      const shouldUpdate = hasNoInitialValue || isMissingInOptions;

      if (shouldUpdate) {
        change(field as string, options[0]?.value);
      }
    },
    [values, change]
  );

  return { forceFieldUpdate };
};

export default useForceUpdate;
